import {
  axisPoints,
  gridPoints,
  labelPoints,
  ringPoints,
  toPolygonString,
} from './radarPath';
import { buildLensComparisonAxes, shortenAxisLabel } from './lensRankRadarPath';
import styles from './LensComparisonRadar.module.css';

const SIZE = 220;
const CENTER = SIZE / 2;
const RADIUS = 78;
const LABEL_RADIUS = RADIUS + 22;
const RING_SCALES = [0.25, 0.5, 0.75, 1];

/**
 * F2 (docs/briefs/morning-2026-07-07/06-feature-opportunities.md, WP-C) —
 * Jason's exact ruling: "this needs to have visual representation like the
 * current spider web graph that we use." Reuses RadarChart.tsx's own SVG
 * geometry (radarPath.ts, unchanged) and theme-token idiom — same visual
 * language, Sage Teal palette — with a SECOND overlaid polygon for the
 * before/after comparison (RadarChart only ever draws one).
 *
 * NO INVENTION: renders exactly the two orderings `lensRanking` actually
 * carries (modelOrder/lensOrder), rank-derived per
 * lensComparisonRadar.ts's own documented, honest transform — no numeric
 * weight field exists in the served shape, so this never claims one. A
 * caller passing fewer than 3 shared achievements (too few to read as a
 * shape) gets the same honest "not enough to draw" empty state RadarChart
 * uses, never a degenerate line/point dressed up as a radar.
 */
export function LensComparisonRadar({
  modelOrder,
  lensOrder,
}: {
  modelOrder: string[];
  lensOrder: string[];
}) {
  const axes = buildLensComparisonAxes(modelOrder, lensOrder);

  if (axes.length < 3) {
    return (
      <div className={styles.empty}>Not enough ranked achievements to draw a comparison.</div>
    );
  }

  // Undefined (an achievement present in only one list) plots as 0 rather
  // than being dropped from the axis set entirely — the axis itself (the
  // achievement) is real; only that ONE polygon has nothing to show there.
  const modelValues = axes.map((a) => a.modelValue ?? 0);
  const lensValues = axes.map((a) => a.lensValue ?? 0);

  const modelPoints = axisPoints(modelValues, CENTER, RADIUS);
  const lensPoints = axisPoints(lensValues, CENTER, RADIUS);
  const outerGrid = gridPoints(axes.length, CENTER, RADIUS);
  const labels = labelPoints(axes.length, CENTER, LABEL_RADIUS);

  return (
    <div className={styles.wrap}>
      <svg
        viewBox={`0 0 ${SIZE} ${SIZE}`}
        width="100%"
        role="img"
        aria-label="Default order vs. axis-lens order, plotted by rank"
      >
        {outerGrid.map((p, i) => (
          <line
            key={`spoke-${axes[i].key}`}
            x1={CENTER}
            y1={CENTER}
            x2={p.x}
            y2={p.y}
            className={styles.spoke}
          />
        ))}

        {RING_SCALES.map((scale) => (
          <polygon
            key={`ring-${scale}`}
            points={toPolygonString(ringPoints(axes.length, CENTER, RADIUS, scale))}
            className={styles.ring}
          />
        ))}

        {/* Default (model) order — the "before" polygon. */}
        <polygon points={toPolygonString(modelPoints)} className={styles.modelFill} />
        <polygon points={toPolygonString(modelPoints)} className={styles.modelStroke} />

        {/* Lens-ranked order — the "after" polygon, drawn on top. */}
        <polygon points={toPolygonString(lensPoints)} className={styles.lensFill} />
        <polygon points={toPolygonString(lensPoints)} className={styles.lensStroke} />
        {lensPoints.map((p, i) => (
          <circle key={`vertex-${axes[i].key}`} cx={p.x} cy={p.y} r={2.6} className={styles.lensVertex} />
        ))}

        {labels.map((p, i) => (
          <text
            key={`label-${axes[i].key}`}
            x={p.x}
            y={p.y}
            textAnchor={labelAnchor(p.x, CENTER)}
            dominantBaseline={labelBaseline(p.y, CENTER)}
            className={styles.label}
          >
            {shortenAxisLabel(axes[i].key)}
          </text>
        ))}
      </svg>
      <div className={styles.legend}>
        <div className={styles.legendRow}>
          <span className={`${styles.legendSwatch} ${styles.legendSwatchModel}`} aria-hidden="true" />
          <span className={styles.legendLabel}>Default order</span>
        </div>
        <div className={styles.legendRow}>
          <span className={`${styles.legendSwatch} ${styles.legendSwatchLens}`} aria-hidden="true" />
          <span className={styles.legendLabel}>Lens-ranked order</span>
        </div>
      </div>
    </div>
  );
}

function labelAnchor(x: number, center: number): 'start' | 'end' | 'middle' {
  const dx = x - center;
  if (Math.abs(dx) < 4) return 'middle';
  return dx > 0 ? 'start' : 'end';
}

function labelBaseline(y: number, center: number): 'auto' | 'hanging' | 'middle' {
  const dy = y - center;
  if (Math.abs(dy) < 4) return 'middle';
  return dy < 0 ? 'auto' : 'hanging';
}
